import React, { useCallback, useContext, useState } from 'react';
import { isUndefined, omitBy } from 'lodash';
import styled from 'styled-components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import {
  ApproveIcon,
  Tooltip,
  ActiveDeclineIcon,
  DeclineIcon,
  FlexColumnCenter,
  ConfirmationModal
} from '@ubeya/shared-web/components';
import ClientTimesheetApprovalSignatureModal from '../../../shared/components/OrderTimeSheetTable/ClientTimesheetApprovalSignatureModal';
import DeclineReasonModal from './DeclineReasonModal';

const IconWrapper = styled(FlexColumnCenter)`
  border-radius: 50%;
  width: 30px;
  height: 30px;
  transition: background 200ms;

  &:hover {
    background: ${({ theme, $hideBackground }) => ($hideBackground ? 'unset' : theme.colors.grey2)};
  }
`;

const ApproveButton = styled(ApproveIcon)`
  min-width: 24px;
  cursor: ${({ $isActive }) => ($isActive ? 'default' : 'pointer')};
`;

const DeclineButton = styled(DeclineIcon)`
  min-width: 24px;
  cursor: pointer;
`;

const ActiveDeclineButton = styled(ActiveDeclineIcon)`
  min-width: 24px;
  cursor: pointer;
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: left;
`;

const ExternalApproveRenderer = ({
  rowData: {
    actualIn,
    actualOut,
    orderId,
    shiftId,
    originSupplierData,
    clientApproved: clientApprovedStatus,
    clientDeclined: clientDeclinedStatus,
    slotId,
    timesheetId,
    supplierBranchId
  },
  columnData: { editOSTimeSheet, mappedSupplierBranches }
}) => {
  const { t } = useContext(ConfigContext);
  const [openDeclineModal, setOpenDeclineModal] = useState(false);
  const [isUndeclineWarningOpen, setIsUndeclineWarningOpen] = useState(false);
  const [openApproveSignatureModal, setOpenApproveSignatureModal] = useState(false);

  const { requireClientTimesheetSignoff } =
    originSupplierData?.timesheetConfig || mappedSupplierBranches?.[supplierBranchId]?.timesheetConfig || {};

  const handleClientReaction = useCallback(
    ({ clientApproved, clientDeclined }) => {
      const timesheet = omitBy({ clientApproved, clientDeclined }, isUndefined);

      editOSTimeSheet({
        slotId,
        orderId,
        shiftId,
        timesheetId,
        timesheet
      });
    },
    [orderId, shiftId, slotId, timesheetId, editOSTimeSheet]
  );

  const onApprove = useCallback(() => {
    if (clientApprovedStatus) return;
    if (requireClientTimesheetSignoff) return setOpenApproveSignatureModal(true);
    handleClientReaction({
      clientApproved: { status: true },
      clientDeclined: null
    });
  }, [clientApprovedStatus, requireClientTimesheetSignoff, handleClientReaction]);

  const onApproveWithSignature = useCallback(
    ({ signature }) => {
      handleClientReaction({
        clientApproved: { status: true, signature },
        clientDeclined: null
      });
    },
    [handleClientReaction]
  );

  const onDeclineWithReason = useCallback(
    ({ clientDeclineReason }) => {
      handleClientReaction({
        clientApproved: null,
        clientDeclined: { status: true, reason: clientDeclineReason }
      });
    },
    [handleClientReaction]
  );

  const onUndecline = useCallback(() => {
    handleClientReaction({
      clientDeclined: null
    });
    setIsUndeclineWarningOpen(false);
  }, [handleClientReaction]);

  const handleCloseApproveModal = useCallback(() => setOpenApproveSignatureModal(false), []);

  const handleShowDeclineModal = useCallback(() => setOpenDeclineModal(true), []);

  const handleCloseDeclineModal = useCallback(() => setOpenDeclineModal(false), []);

  const handleShowUndeclineModal = useCallback(() => setIsUndeclineWarningOpen(true), []);

  const handleCloseUndeclineModal = useCallback(() => setIsUndeclineWarningOpen(false), []);

  if (!timesheetId || !actualIn || !actualOut) {
    return <></>;
  }

  return (
    <Wrapper>
      <IconWrapper $hideBackground={clientApprovedStatus}>
        <ApproveButton
          $isActive={clientApprovedStatus}
          onClick={onApprove}
          data-testid="timesheet-approve-button"
          data-approved={String(!!clientApprovedStatus)}
        />
      </IconWrapper>

      <IconWrapper>
        {clientDeclinedStatus ? (
          <Tooltip
            body={`${t('declineReason')}: ${clientDeclinedStatus.reason}`}
            disabled={!clientDeclinedStatus.reason}>
            <ActiveDeclineButton onClick={handleShowUndeclineModal} />
          </Tooltip>
        ) : (
          <DeclineButton onClick={handleShowDeclineModal} />
        )}
      </IconWrapper>

      {openApproveSignatureModal && (
        <ClientTimesheetApprovalSignatureModal
          handleClientApprove={onApproveWithSignature}
          onClose={handleCloseApproveModal}
        />
      )}

      {openDeclineModal && (
        <DeclineReasonModal handleClientDecline={onDeclineWithReason} onClose={handleCloseDeclineModal} />
      )}

      {isUndeclineWarningOpen && (
        <ConfirmationModal
          title={t('areYouSureUndeclineTimesheet')}
          onConfirm={onUndecline}
          onClose={handleCloseUndeclineModal}
          confirmText={t('ok')}
          showCancelButton
          usePortal
        />
      )}
    </Wrapper>
  );
};

export default ExternalApproveRenderer;
